import { FieldProps } from "../../../InputFieldsController/InputFieldsController.utils";
import {
  ClientTypeCode,
  FieldTypeOption,
  ReferenceDataOption,
  SelectedFieldsRecord,
  SystemField,
} from "./SystemFieldDrawer.types";
import { filterCreatableCategoryOptions } from "../../../../utilities/customFieldCategory.utils";

export const DEFAULT_SELECTED_FIELD_CODES: Record<ClientTypeCode, string[]> = {
  INDIVIDUAL: [
    "client_name",
    "parent_client_ref",
    "contact_name",
    "client_phone_office",
    "client_phone_mobile",
    "client_fax",
    "client_email",
    "federal_id_number",
    "social_security_number",
    "spouse_ssn",
  ],
  ENTITY: [
    "office",
    "primary_service_type",
    "sub_niche",
    "ownership",
    "entity_wealth",
    "entity_search",
    "entity_envision",
    "entity_outsourcing",
    "tow_aa",
    "tow_consulting",
    "tow_tax",
  ],
};

export const FIELD_TYPE_OPTIONS: FieldTypeOption[] = [
  { id: "text", label: "Text", value: "text" },
  { id: "password", label: "Password", value: "password" },
  { id: "numberinput", label: "Number", value: "numberinput" },
  { id: "checkbox", label: "Checkbox", value: "checkbox" },
  { id: "select", label: "Dropdown", value: "select" },
  { id: "date", label: "Date", value: "date" },
  { id: "file", label: "File", value: "file" },
];

export const isUnmappedSystemField = (field: FieldProps): boolean =>
  field.systemStatus === "unmapped" ||
  !field.allfields_id ||
  field.allfields_id === 0 ||
  String(field.allfields_key ?? "").trim() === "" ||
  /^custom(_field)?\d*$/i.test(String(field.allfields_code ?? ""));

export const normalizeSearch = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ");

export const getFieldDisplayName = (field: FieldProps): string =>
  String(field.customLabel || field.label || field.allfields_key || field.id || "Untitled Field");

export const filterSystemFields = (
  fields: SystemField[],
  searchText: string
): SystemField[] => {
  const query = normalizeSearch(searchText);
  if (!query) return fields;

  return fields.filter((field) => {
    const haystack = normalizeSearch(
      `${field.field_name} ${field.field_code} ${field.category} ${field.type}`
    );
    return haystack.includes(query);
  });
};

export const toCategoryOptions = (records: any[]): ReferenceDataOption[] =>
  filterCreatableCategoryOptions(
    records
      .map((record) => {
        const value = String(record?.ReferenceDataValue ?? record?.value ?? record?.label ?? "").trim();
        return value ? { label: value, value } : null;
      })
      .filter(Boolean) as ReferenceDataOption[]
  );

export const resolveClientTypesFromFields = (fields: FieldProps[]): ClientTypeCode[] => {
  const clientTypeField = fields.find((field) => {
    const keys = [
      field.allfields_key,
      field.allfields_code,
      field.code,
      field.label,
    ].map((value) => normalizeSearch(value).replace(/\s+/g, ""));

    return keys.some((key) => key === "clienttype" || key === "clienttypecode");
  });

  const value = normalizeSearch(clientTypeField?.value).replace(/\s+/g, "");
  if (value.includes("entity")) return ["ENTITY"];
  if (value.includes("individual")) return ["INDIVIDUAL"];

  return ["INDIVIDUAL", "ENTITY"];
};

export const buildSelectedCodesForClientType = (
  selectedRecords: SelectedFieldsRecord[],
  clientType: ClientTypeCode,
  fieldCode: string
): string[] => {
  const record = selectedRecords.find(
    (item) => String(item.ClientType ?? "").trim().toUpperCase() === clientType
  );

  const currentCodes = record?.codes?.length
    ? record.codes
    : DEFAULT_SELECTED_FIELD_CODES[clientType];

  return Array.from(new Set([...currentCodes, fieldCode].filter(Boolean)));
};

export const patchFieldWithSystemField = (
  field: FieldProps,
  systemField: SystemField
): FieldProps => ({
  ...field,
  type: systemField.type || field.type,
  label: systemField.field_name || field.label,
  allfields_id: systemField.id,
  allfields_key: systemField.field_name || field.allfields_key,
  allfields_code: systemField.field_code || field.allfields_code,
  allfields_category: systemField.category || field.allfields_category,
  options: Array.isArray(systemField.options) ? systemField.options : field.options,
  allOptions: Array.isArray(systemField.options) ? systemField.options : field.allOptions,
  systemStatus: undefined,
});

export const findCreatedField = (
  allFields: SystemField[],
  fieldName: string,
  fieldCode?: string
): SystemField | undefined => {
  const nameKey = normalizeSearch(fieldName);
  const codeKey = normalizeSearch(fieldCode);

  return allFields.find((field) => {
    const candidateName = normalizeSearch(field.field_name);
    const candidateCode = normalizeSearch(field.field_code);
    return (
      (codeKey && candidateCode === codeKey) ||
      candidateName === nameKey
    );
  });
};
